import { useCallback, useMemo, useState } from 'react';
import axios from 'axios';

import { reportAxiosError } from '~/Utils';

export type AvailableCoveredObjectType = { id: string; desc?: string; icon?: string };

type UseAddCoveredObjectFlowArgs = {
  isAutoClaim: boolean;
  // The lob-config covered_objects list for the current LOB. Auto LOBs typically
  // come in with this empty; the hook overlays a `covered_vehicle` entry so the
  // resulting `availableTypes` is what the type selector / dialogs consume.
  rawAvailableTypes?: AvailableCoveredObjectType[];
  claimId: number;
  isManualPolicy: boolean;
  onAsyncClaimUpdate: () => Promise<void> | void;
  // Fired after a successful create. V2 uses this to bump its per-type
  // refresh token so the affected table re-fetches; V1's claim payload
  // already carries the rows so it can ignore this.
  onCreated?: (typeId: string) => void;
};

type UseAddCoveredObjectFlowResult = {
  // Auto LOBs always expose a single covered_vehicle row regardless of lob
  // config; non-auto returns the raw list.
  availableTypes: AvailableCoveredObjectType[];
  // Type selected for the Add dialog. Null when no dialog is open.
  addType: string | null;
  showTypeSelector: boolean;
  isCreating: boolean;
  // Click handler for the "Add Covered Object" button. Auto / single-type
  // claims open the dialog directly with the only type pre-selected; multi-
  // type LOBs first open the type selector.
  handleAdd: () => void;
  // Wire to CoveredObjectTypeSelector.onTypeSelected.
  handleAddTypeSelected: (typeId: string) => void;
  // Wire to EditCoveredEntityDialog.onSaveClick.
  handleCreate: (values: Record<string, unknown>) => Promise<void>;
  closeTypeSelector: () => void;
  // Resets dialog state — wire to the dialog's onClose.
  closeAddDialog: () => void;
};

// Shared Add-Covered-Object flow. V1 (GenericPolicyPage) and V2
// (CoveredObjectsTab) both consume it. Encapsulates: the auto-vs-LOB type
// list, the click-routing between direct-open and type-selector-first, the
// POST to the new_covered_object endpoint, and the surrounding loading /
// dialog state. The caller still owns the dialog rendering (V1 uses
// EditCoveredEntityDialog directly with locally-resolved field configs;
// V2 wraps it in AddCoveredObjectDialog which resolves fields per addType).
export const useAddCoveredObjectFlow = ({
  isAutoClaim,
  rawAvailableTypes,
  claimId,
  isManualPolicy,
  onAsyncClaimUpdate,
  onCreated,
}: UseAddCoveredObjectFlowArgs): UseAddCoveredObjectFlowResult => {
  const availableTypes = useMemo<AvailableCoveredObjectType[]>(
    () => (isAutoClaim ? [{ id: 'covered_vehicle', desc: 'Covered Vehicle' }] : (rawAvailableTypes ?? [])),
    [isAutoClaim, rawAvailableTypes]
  );

  const [addType, setAddType] = useState<string | null>(null);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handleAdd = useCallback(() => {
    if (availableTypes.length === 1) {
      setAddType(availableTypes[0].id);
    } else if (availableTypes.length > 1) {
      setShowTypeSelector(true);
    }
  }, [availableTypes]);

  const handleAddTypeSelected = useCallback((typeId: string) => {
    setShowTypeSelector(false);
    setAddType(typeId);
  }, []);

  const closeTypeSelector = useCallback(() => setShowTypeSelector(false), []);
  const closeAddDialog = useCallback(() => setAddType(null), []);

  const handleCreate = useCallback(
    async (values: Record<string, unknown>) => {
      if (!addType) return;
      const policyPrefix = isManualPolicy ? '' : 'api_';
      setIsCreating(true);
      try {
        await axios.post(`/api/v1/claims/${claimId}/${policyPrefix}policy/new_covered_object`, {
          ...values,
          type: addType,
        });
        await onAsyncClaimUpdate();
        onCreated?.(addType);
        setAddType(null);
      } catch (error) {
        reportAxiosError(error);
        throw error;
      } finally {
        setIsCreating(false);
      }
    },
    [addType, claimId, isManualPolicy, onAsyncClaimUpdate, onCreated]
  );

  return {
    availableTypes,
    addType,
    showTypeSelector,
    isCreating,
    handleAdd,
    handleAddTypeSelected,
    handleCreate,
    closeTypeSelector,
    closeAddDialog,
  };
};
